/**
 * ============================================================================
 * ItemPermissionPage — 화면 항목(Item) 권한 관리 (2-Tab 구조)
 * ============================================================================
 *
 * 【역할】
 *   메뉴별 UI 항목(버튼/컬럼/필드/탭) 을 정의하고, 역할별 visible/enabled
 *   매트릭스를 관리. RolePermission 이 메뉴 단위 권한이라면 본 화면은 항목
 *   수준의 세분화된 권한 제어 (예: 'CustomerListPage 의 SaveBtn 만 차단').
 *
 * 【주요 기능】
 *   - Tab 1 (항목 정의): 메뉴 선택 → 해당 메뉴의 항목 CRUD
 *     (itemId/itemName/itemType=button|field|column|tab/sortOrder/useYn)
 *   - Tab 2 (항목 권한): 역할+메뉴 선택 → 항목별 canVisible/canEnabled 토글
 *   - 헤더 체크박스로 컬럼 전체 일괄 토글
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=adminItemPerm/searchMenuList)    — 메뉴 목록
 *   - POST /api/v1/transaction (service=adminItemPerm/searchItems)       — 항목 정의
 *   - POST /api/v1/transaction (service=adminItemPerm/searchPermissions) — 항목 권한
 *   - POST /api/v1/transaction (service=adminItemPerm/saveItems)         — 항목 저장
 *   - POST /api/v1/transaction (service=adminItemPerm/savePermissions)   — 권한 저장
 *   - POST /api/v1/transaction (service=adminRole/searchRoleList)        — 역할 목록
 *
 * 【DataSet/Hook】
 *   - useDataSet<MenuRow>()     — menuDs : 메뉴 목록 (Select 옵션)
 *   - useDataSet<RoleRow>()     — roleDs : 역할 목록 (Select 옵션)
 *   - useDataSet<ItemRow>()     — itemDs : 항목 정의 (PK: menuId+itemId)
 *   - useDataSet<ItemPermRow>() — permDs : 항목 권한 (PK: roleId+menuId+itemId)
 *   - useTransaction()          — RPC 통합 호출
 *   - useLabel()                — i18n 라벨(t)
 *
 * 【사용처】 router/index.tsx — `{ path: '/admin/item-perm', element: <ItemPermissionPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageItemPermission.vue`
 * 【의존성】 antd Checkbox/Input/Select/Space/Tabs/Tag, framework hooks
 */
import { useEffect, useMemo, useState } from 'react'
import { Checkbox, Input, Select, Space, Tabs, Tag } from 'antd'
import type { ColumnType } from 'antd/es/table'
import CrudToolbar from '@/components/common/CrudToolbar'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'

/** 메뉴 1행 — Select 옵션 데이터. */
interface MenuRow {
  /** 메뉴 PK. */
  menuId: string
  /** 메뉴 표시명. */
  menuName: string
}
/** 역할 1행 — Select 옵션 데이터. */
interface RoleRow {
  /** 역할 PK. */
  roleId: string
  /** 역할 표시명. */
  roleName: string
}
/** 항목 정의 1행 — Tab 1 그리드. */
interface ItemRow {
  /** 소속 메뉴 PK. */
  menuId: string
  /** 항목 PK 일부 (예: "BTN_SAVE", "COL_NAME"). */
  itemId: string
  /** 항목 표시명. */
  itemName: string
  /** 항목 유형 (예: "button"·"field"·"column"·"tab"). */
  itemType?: string
  /** 정렬 순서 (오름차순). */
  sortOrder?: number
  /** 사용여부 ('Y'/'N'). */
  useYn: 'Y' | 'N'
}
/** 항목 권한 1행 — Tab 2 그리드 (역할+메뉴 컨텍스트). */
interface ItemPermRow {
  /** 역할 PK (FK). */
  roleId: string
  /** 메뉴 PK (FK). */
  menuId: string
  /** 항목 PK (FK → ItemRow.itemId). */
  itemId: string
  /** 항목명 (서버 join). */
  itemName?: string
  /** 항목 유형 (서버 join). */
  itemType?: string
  /** 가시성 권한 (1=보임 / 0=숨김). */
  canVisible: number
  /** 활성화 권한 (1=enabled / 0=disabled). */
  canEnabled: number
}

/** 0/1/'Y'/true 등 다양한 truthy 표현을 boolean 으로 정규화. */
const isOn = (v: unknown) => v === 1 || v === '1' || v === true || v === 'Y'

/**
 * ItemPermissionPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function ItemPermissionPage() {
  const menuDs = useDataSet<MenuRow>()
  const roleDs = useDataSet<RoleRow>()
  const itemDs = useDataSet<ItemRow>()
  const permDs = useDataSet<ItemPermRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  const [tab, setTab] = useState('items')
  const [menuIdTab1, setMenuIdTab1] = useState('')
  const [menuIdTab2, setMenuIdTab2] = useState('')
  const [roleIdTab2, setRoleIdTab2] = useState('')

  /** 메뉴 목록 조회 — Select 옵션용 (페이지 진입 시). */
  const searchMenus = () =>
    transaction({
      id: 'menu_search',
      service: 'adminItemPerm/searchMenuList',
      outds: { ds_menuList: menuDs }
    })
  /** 역할 목록 조회 — Tab 2 Select 옵션용 (페이지 진입 시). */
  const searchRoles = () =>
    transaction({
      id: 'role_search',
      service: 'adminRole/searchRoleList',
      outds: { ds_role: roleDs }
    })

  /**
   * 항목 정의 조회 — adminItemPerm/searchItems → itemDs.
   * @param mid 외부 menuId (생략 시 state)
   * 메뉴 미선택 시 시드 데이터 노출용 전체 조회 (빈 ds_search).
   */
  const searchItems = (mid?: string) => {
    const m = mid ?? menuIdTab1
    // 메뉴 미선택 시 모든 항목 조회 (페이지 첫 진입 시 빈 화면 방지)
    return transaction({
      id: 'item_search',
      service: 'adminItemPerm/searchItems',
      inds: { ds_search: m ? { menuId: m } : {} },
      outds: { ds_itemList: itemDs }
    })
  }

  /**
   * 항목 권한 조회 — adminItemPerm/searchPermissions → permDs.
   * 역할 + 메뉴 모두 선택되어야 호출, 어느 한쪽이라도 미선택 시 무동작.
   */
  const searchPerms = (rid?: string, mid?: string) => {
    const r = rid ?? roleIdTab2
    const m = mid ?? menuIdTab2
    if (!r || !m) return
    return transaction({
      id: 'perm_search',
      service: 'adminItemPerm/searchPermissions',
      inds: { ds_search: { roleId: r, menuId: m } },
      outds: { ds_itemPermList: permDs }
    })
  }

  useEffect(() => {
    searchMenus()
    searchRoles()
    // 페이지 진입 시 전체 항목 자동 조회 (메뉴 미선택 상태에서도 시드 데이터 노출)
    searchItems()
  }, [])

  /** 항목 정의 추가 — Tab 1 메뉴 선택 후 빈 행 push (itemType='button' 기본). */
  const addItem = () => {
    if (!menuIdTab1) return
    itemDs.addRow({
      menuId: menuIdTab1,
      itemId: '',
      itemName: '',
      itemType: 'button',
      sortOrder: 0,
      useYn: 'Y'
    })
  }
  /** 선택 항목 삭제 — DataSet C/U/D 추적. */
  const delItem = () => itemDs.selectedIndex >= 0 && itemDs.deleteRow(itemDs.selectedIndex)
  /** 항목 정의 저장 — adminItemPerm/saveItems. 성공 시 재조회. */
  const saveItems = async () => {
    const res = await transaction({
      id: 'item_save',
      service: 'adminItemPerm/saveItems',
      action: 'save',
      inds: { ds_itemList: itemDs },
      outds: { ds_itemList: itemDs }
    })
    if (res.success) searchItems()
  }

  /**
   * 항목 권한 저장 — adminItemPerm/savePermissions.
   * 컨텍스트(roleId+menuId)는 ds_search 로, 변경분은 ds_itemPermList 로 전송.
   */
  const savePerms = async () => {
    const res = await transaction({
      id: 'perm_save',
      service: 'adminItemPerm/savePermissions',
      action: 'save',
      inds: { ds_search: { roleId: roleIdTab2, menuId: menuIdTab2 }, ds_itemPermList: permDs }
    })
    if (res.success) searchPerms()
  }

  /** 단일 셀 체크박스 토글. */
  const toggleCell = (idx: number, field: 'canVisible' | 'canEnabled', checked: boolean) => {
    permDs.updateRow(idx, { [field]: checked ? 1 : 0 } as Partial<ItemPermRow>)
  }
  /** 컬럼 헤더 체크박스 토글 — 표시 중 모든 행 일괄 set. */
  const toggleAll = (field: 'canVisible' | 'canEnabled', checked: boolean) => {
    permDs
      .getVisibleRows()
      .forEach((_r, i) => permDs.updateRow(i, { [field]: checked ? 1 : 0 } as Partial<ItemPermRow>))
  }
  /** 헤더 'checked' 상태 — 모든 행이 ON 인지. */
  const allChecked = (field: 'canVisible' | 'canEnabled') => {
    const rows = permDs.getVisibleRows()
    return rows.length > 0 && rows.every((r) => isOn(r.data[field]))
  }

  const menuOptions = menuDs.rows.map((r) => ({
    label: `${r.data.menuName} (${r.data.menuId})`,
    value: r.data.menuId
  }))
  const roleOptions = roleDs.rows.map((r) => ({
    label: `${r.data.roleName} (${r.data.roleId})`,
    value: r.data.roleId
  }))

  const itemColumns = useMemo(() => {
    const specs: ColumnSpec<TrackedRow<ItemRow>>[] = [
      {
        key: 'COL_ITEM_ID',
        dataIndex: ['data', 'itemId'],
        width: 180,
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.itemId}
            disabled={r._state !== 'C'}
            onChange={(e) => itemDs.updateRow(i, { itemId: e.target.value })}
          />
        )
      },
      {
        key: 'COL_ITEM_NAME',
        dataIndex: ['data', 'itemName'],
        width: 200,
        render: (_v, r, i) => (
          <Input
            size="small"
            value={r.data.itemName}
            onChange={(e) => itemDs.updateRow(i, { itemName: e.target.value })}
          />
        )
      },
      {
        key: 'COL_ITEM_TYPE',
        dataIndex: ['data', 'itemType'],
        width: 140,
        render: (_v, r, i) => (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={r.data.itemType}
            options={['button', 'field', 'column', 'tab'].map((x) => ({ label: x, value: x }))}
            onChange={(v) => itemDs.updateRow(i, { itemType: v })}
          />
        )
      },
      {
        key: 'COL_SORT_ORDER',
        dataIndex: ['data', 'sortOrder'],
        width: 90,
        render: (_v, r, i) => (
          <Input
            size="small"
            type="number"
            value={r.data.sortOrder}
            onChange={(e) => itemDs.updateRow(i, { sortOrder: Number(e.target.value) })}
          />
        )
      },
      {
        key: 'COL_USE_YN',
        dataIndex: ['data', 'useYn'],
        width: 80,
        render: (_v, r, i) => (
          <Select<'Y' | 'N'>
            size="small"
            style={{ width: '100%' }}
            value={r.data.useYn}
            options={[
              { label: t('LBL_YES', '사용'), value: 'Y' },
              { label: t('LBL_NO', '미사용'), value: 'N' }
            ]}
            onChange={(v) => itemDs.updateRow(i, { useYn: v })}
          />
        )
      },
      {
        key: 'COL_STATUS',
        dataIndex: '_state',
        width: 70,
        render: (s) =>
          s === 'C' ? (
            <Tag color="green">{t('TTL_ADD', '신규')}</Tag>
          ) : s === 'U' ? (
            <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>
          ) : null
      }
    ]
    return makeColumns(specs, t)
  }, [t, itemDs])

  const permColumns = useMemo(() => {
    type PermTRow = TrackedRow<ItemPermRow>
    const baseSpecs: ColumnSpec<PermTRow>[] = [
      { key: 'COL_ITEM_ID', dataIndex: ['data', 'itemId'], width: 180 },
      { key: 'COL_ITEM_NAME', dataIndex: ['data', 'itemName'], width: 200 },
      { key: 'COL_ITEM_TYPE', dataIndex: ['data', 'itemType'], width: 110 }
    ]
    const base = makeColumns(baseSpecs, t)
    const visibleCol: ColumnType<PermTRow> = {
      key: 'COL_CAN_VISIBLE',
      title: (
        <Checkbox
          checked={allChecked('canVisible')}
          onChange={(e) => toggleAll('canVisible', e.target.checked)}
        >
          {t('COL_CAN_VISIBLE', '가시')}
        </Checkbox>
      ),
      width: 100,
      align: 'center',
      render: (_v: unknown, r: PermTRow, i: number) => (
        <Checkbox
          checked={isOn(r.data.canVisible)}
          onChange={(e) => toggleCell(i, 'canVisible', e.target.checked)}
        />
      )
    }
    const enabledCol: ColumnType<PermTRow> = {
      key: 'COL_CAN_ENABLED',
      title: (
        <Checkbox
          checked={allChecked('canEnabled')}
          onChange={(e) => toggleAll('canEnabled', e.target.checked)}
        >
          {t('COL_CAN_ENABLED', '활성')}
        </Checkbox>
      ),
      width: 100,
      align: 'center',
      render: (_v: unknown, r: PermTRow, i: number) => (
        <Checkbox
          checked={isOn(r.data.canEnabled)}
          onChange={(e) => toggleCell(i, 'canEnabled', e.target.checked)}
        />
      )
    }
    const statusCol: ColumnType<PermTRow> = {
      title: t('COL_STATUS', '상태'),
      dataIndex: '_state',
      width: 70,
      render: (s: unknown) => (s === 'U' ? <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag> : null)
    }
    return [...base, visibleCol, enabledCol, statusCol]
  }, [t, permDs.rows])

  // 각 Tab 내부 콘텐츠 wrapper — 헤더(툴바) + DataTable flex-fill
  const tabContentStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    flex: 1,
    minHeight: 0,
    height: '100%',
    gap: 8
  }
  const toolbarStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexShrink: 0
  }

  return (
    <PageContainer>
      <div
        className="fw-page-grid"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'items',
              label: t('TAB_ITEMS', '항목 정의'),
              children: (
                <div style={tabContentStyle}>
                  <div style={toolbarStyle}>
                    <Space>
                      <span style={{ whiteSpace: 'nowrap' }}>{t('LBL_MENU_COLON', '메뉴:')}</span>
                      <Select
                        style={{ width: 260 }}
                        value={menuIdTab1 || undefined}
                        options={menuOptions}
                        onChange={(v) => {
                          setMenuIdTab1(v)
                          searchItems(v)
                        }}
                        placeholder={t('PH_MENU_SELECT', '메뉴 선택')}
                      />
                    </Space>
                    <CrudToolbar
                      onSearch={() => searchItems()}
                      onAdd={addItem}
                      onDelete={delItem}
                      onSave={saveItems}
                      dirty={itemDs.isDirty}
                    />
                  </div>
                  <DataTable
                    rowKey="_rowId"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DataTable generic 와 useDataset getVisibleRows 의 TrackedRow 타입 호환을 위해 any 캐스팅 필요
                    dataSource={itemDs.getVisibleRows() as any}
                    columns={itemColumns}
                    scroll={{ x: 'max-content' }}
                    rowSelection={{
                      type: 'radio',
                      selectedRowKeys: itemDs.selectedRow ? [itemDs.selectedRow._rowId] : [],
                      onChange: (keys) => {
                        const idx = itemDs.rows.findIndex((r) => r._rowId === keys[0])
                        itemDs.setSelectedIndex(idx)
                      }
                    }}
                  />
                </div>
              )
            },
            {
              key: 'perms',
              label: t('TAB_PERMS', '항목 권한'),
              children: (
                <div style={tabContentStyle}>
                  <div style={toolbarStyle}>
                    <Space>
                      <span style={{ whiteSpace: 'nowrap' }}>{t('LBL_ROLE_COLON', '역할:')}</span>
                      <Select
                        style={{ width: 220 }}
                        value={roleIdTab2 || undefined}
                        options={roleOptions}
                        onChange={(v) => {
                          setRoleIdTab2(v)
                          searchPerms(v, menuIdTab2)
                        }}
                        placeholder={t('PH_ROLE_SELECT', '역할 선택')}
                      />
                      <span style={{ whiteSpace: 'nowrap' }}>{t('LBL_MENU_COLON', '메뉴:')}</span>
                      <Select
                        style={{ width: 260 }}
                        value={menuIdTab2 || undefined}
                        options={menuOptions}
                        onChange={(v) => {
                          setMenuIdTab2(v)
                          searchPerms(roleIdTab2, v)
                        }}
                        placeholder={t('PH_MENU_SELECT', '메뉴 선택')}
                      />
                    </Space>
                    <CrudToolbar
                      onSearch={() => searchPerms()}
                      onSave={savePerms}
                      dirty={permDs.isDirty}
                    />
                  </div>
                  <DataTable
                    rowKey="_rowId"
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- DataTable generic 와 useDataset getVisibleRows 의 TrackedRow 타입 호환을 위해 any 캐스팅 필요
                    dataSource={permDs.getVisibleRows() as any}
                    columns={permColumns}
                    scroll={{ x: 'max-content' }}
                  />
                </div>
              )
            }
          ]}
        />
      </div>
    </PageContainer>
  )
}
